/**
 * @zendir/ui - SpatialSelectionContext
 *
 * Optional shared selection state for spatial dashboards. Wrap a tree
 * containing `<ZenSpace3D />`, `<GroundTrackMap />`,
 * `<ObjectInventoryPanel />`, etc. — they cross-talk via the context
 * and stay synchronised without prop-drilling:
 *
 *   <SpatialSelectionProvider>
 *     <ObjectInventoryPanel objects={scene.objects} />  // writes selection
 *     <ZenSpace3D satellites={...} />                   // reads selection
 *     <GroundTrackMap satellites={tracks} />            // reads selection
 *   </SpatialSelectionProvider>
 *
 * The provider is **opt-in**. Components that consume the context use
 * `useSpatialSelection()` which returns `null` outside a provider —
 * that's how every component stays standalone-usable. No coupling.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SpatialSelectionContextValue {
  /** ID of the currently selected object, or null if nothing is selected. */
  selectedId: string | null;
  /** Set the selection (or clear with `null`). */
  setSelectedId: (id: string | null) => void;
  /** Toggle selection: select the id if it isn't selected, clear otherwise. */
  toggleSelection: (id: string) => void;
  /** Convenience: clear any current selection. */
  clearSelection: () => void;
}

const SpatialSelectionContextInternal =
  createContext<SpatialSelectionContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export interface SpatialSelectionProviderProps {
  children: ReactNode;
  /** Initial selected id. Default null (nothing selected). */
  defaultSelectedId?: string | null;
  /** Controlled mode: when set, the provider mirrors this value instead
   *  of owning state. Pair with `onSelectionChange`. */
  selectedId?: string | null;
  /** Called whenever the selection changes (uncontrolled or controlled). */
  onSelectionChange?: (id: string | null) => void;
}

export function SpatialSelectionProvider({
  children,
  defaultSelectedId = null,
  selectedId: controlledId,
  onSelectionChange,
}: SpatialSelectionProviderProps) {
  const [internalId, setInternalId] = useState<string | null>(
    defaultSelectedId,
  );
  const isControlled = controlledId !== undefined;
  const selectedId = isControlled ? controlledId : internalId;

  const setSelectedId = useCallback(
    (id: string | null) => {
      if (!isControlled) setInternalId(id);
      onSelectionChange?.(id);
    },
    [isControlled, onSelectionChange],
  );

  const toggleSelection = useCallback(
    (id: string) => {
      const next = selectedId === id ? null : id;
      if (!isControlled) setInternalId(next);
      onSelectionChange?.(next);
    },
    [selectedId, isControlled, onSelectionChange],
  );

  const clearSelection = useCallback(() => {
    if (!isControlled) setInternalId(null);
    onSelectionChange?.(null);
  }, [isControlled, onSelectionChange]);

  const value = useMemo<SpatialSelectionContextValue>(
    () => ({ selectedId, setSelectedId, toggleSelection, clearSelection }),
    [selectedId, setSelectedId, toggleSelection, clearSelection],
  );

  return (
    <SpatialSelectionContextInternal.Provider value={value}>
      {children}
    </SpatialSelectionContextInternal.Provider>
  );
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/**
 * Read the spatial-selection context. Returns `null` when used outside
 * a `<SpatialSelectionProvider>` — components MUST handle that case so
 * they stay standalone-usable. Treat the provider as a progressive
 * enhancement, never a hard dependency.
 *
 *   const ctx = useSpatialSelection();
 *   const selectedId = ctx?.selectedId ?? null;        // safe outside provider
 *   const onSelect   = ctx?.setSelectedId ?? noop;      // safe outside provider
 */
export function useSpatialSelection(): SpatialSelectionContextValue | null {
  return useContext(SpatialSelectionContextInternal);
}

/**
 * Strict variant — throws if used outside a provider. Useful in the
 * provider's own children when the dependency is intentional.
 */
export function useSpatialSelectionStrict(): SpatialSelectionContextValue {
  const ctx = useContext(SpatialSelectionContextInternal);
  if (!ctx) {
    throw new Error(
      "useSpatialSelectionStrict() called outside <SpatialSelectionProvider>. " +
        "Wrap the tree, or use useSpatialSelection() (returns null) for opt-in usage.",
    );
  }
  return ctx;
}

export default SpatialSelectionProvider;
